import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import type { Role } from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { DataSurface } from './DataSurface.js'

/**
 * The shop's policy numbers: the discount limit for each role, the return window and the cut-out
 * allowance. `POS-008a`, `POS-006`, `CAT-015`; `decisions/0004`.
 *
 * **Nothing here has a default, and a blank is a real answer.** All three ship null and null means
 * the shop has decided nothing, so the rule is inert: no discount limit, returns go down the
 * goodwill path, no cut-out check. A blank field is sent as `null`, which clears the setting.
 * Zero is a different thing (a window of same-day only, a 0 percent limit, no allowance) and is
 * sent as 0.
 *
 * **One screen for the three, on `PATCH /settings/policies`.** The form is the whole state, so every
 * save sends all three fields and `expectedVersion`. The server leaves a field it was not sent
 * alone, but this screen always has all three to send.
 *
 * **Every refusal the server can give is checked here first** (`transport.ts` holds a 422 at the
 * head of the queue and stops payment behind it). The bounds are the service's own: a percentage
 * from 0 to 100 with at most two decimals, 0 to 3,650 days, 0 to 1,000 tenths of a millimetre.
 *
 * **Owner alone** (`tenant_settings_policies`). Without the row nothing is read.
 */

export interface PoliciesRow {
  /** Role to percentage string. Absent roles have no limit. */
  readonly discountLimitByRole: Readonly<Partial<Record<Role, string>>> | null
  readonly returnWindowDays: number | null
  readonly cutoutEdgingAllowanceTenthsMm: number | null
  readonly version: number
}

export interface TenantPoliciesProps {
  readonly canManage: boolean
  /** Must throw on a failed read: a `null` row has no version to save against. Referentially stable. */
  readonly loadPolicies: () => Promise<PoliciesRow>
  readonly onWritten?: () => void
}

export const POLICIES_PATH = '/settings/policies'
export const MAX_WINDOW_DAYS = 3650
export const MAX_ALLOWANCE_TENTHS = 1000

/** Literal keys, because `i18n-static.test.ts` scans for key literals. */
const ROLE_FIELDS: readonly { readonly role: Role; readonly key: MessageKey }[] = [
  { role: 'owner', key: 'policies.role.owner' },
  { role: 'manager', key: 'policies.role.manager' },
  { role: 'dispenser', key: 'policies.role.dispenser' },
  { role: 'cashier', key: 'policies.role.cashier' },
  { role: 'optician', key: 'policies.role.optician' },
  { role: 'accountant', key: 'policies.role.accountant' },
  { role: 'read_only', key: 'policies.role.read_only' },
]

interface Draft {
  readonly limits: Readonly<Record<string, string>>
  readonly window: string
  readonly allowance: string
}

const EMPTY: Draft = { limits: {}, window: '', allowance: '' }

const draftOf = (row: PoliciesRow): Draft => ({
  limits: { ...(row.discountLimitByRole ?? {}) } as Record<string, string>,
  window: row.returnWindowDays === null ? '' : String(row.returnWindowDays),
  allowance:
    row.cutoutEdgingAllowanceTenthsMm === null ? '' : String(row.cutoutEdgingAllowanceTenthsMm),
})

/** 0 to 100, at most two decimals: the server's own rule. */
const PERCENT = /^(\d{1,3})(\.\d{1,2})?$/
const validPercent = (value: string): boolean =>
  PERCENT.test(value) && Number(value) >= 0 && Number(value) <= 100

const wholeIn = (value: string, max: number): boolean =>
  /^\d{1,9}$/.test(value) && Number(value) <= max

export function TenantPolicies({
  canManage,
  loadPolicies,
  onWritten,
}: TenantPoliciesProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()

  const [row, setRow] = useState<PoliciesRow | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  const generation = useRef(0)

  const refresh = useCallback(() => {
    generation.current += 1
    const mine = generation.current
    loadPolicies().then(
      (found) => {
        if (generation.current !== mine) return
        setRow(found)
        setDraft(draftOf(found))
        setLoadFailed(false)
      },
      () => {
        if (generation.current !== mine) return
        setRow(null)
        setLoadFailed(true)
      },
    )
  }, [loadPolicies])

  useEffect(() => {
    if (canManage) refresh()
  }, [canManage, refresh])

  /** A policy change from an earlier session still queued: saving over it would carry a stale version. */
  const stillWaiting = snapshot.writes.some(
    (write) => write.path === POLICIES_PATH && write.status !== 'sent',
  )

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setError(null)
    setNotice(t('policies.saved'))
    onWritten?.()
  })

  useEffect(() => {
    if (writeId === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) setWriteId(null)
  }, [snapshot, writeId])

  // Read again the moment nothing of this screen's is waiting, so the next save has the new version.
  const wasWaiting = useRef(false)
  useEffect(() => {
    if (wasWaiting.current && !stillWaiting && canManage) refresh()
    wasWaiting.current = stillWaiting
  }, [stillWaiting, canManage, refresh])
  const busy = writeId !== null || stillWaiting

  const edit = (change: Partial<Draft>): void => {
    setDraft((was) => ({ ...was, ...change }))
    setError(null)
    setNotice(null)
  }

  const shape = (): { readonly error: string } | { readonly body: Record<string, unknown> } => {
    if (row === null) return { error: t('policies.loadFailed') }
    const limits: Record<string, string> = {}
    for (const field of ROLE_FIELDS) {
      const value = (draft.limits[field.role] ?? '').trim()
      if (value === '') continue
      if (!validPercent(value)) {
        return { error: t('policies.limitInvalid', { role: t(field.key) }) }
      }
      limits[field.role] = value
    }
    const window = draft.window.trim()
    if (window !== '' && !wholeIn(window, MAX_WINDOW_DAYS)) {
      return { error: t('policies.windowInvalid', { max: MAX_WINDOW_DAYS }) }
    }
    const allowance = draft.allowance.trim()
    if (allowance !== '' && !wholeIn(allowance, MAX_ALLOWANCE_TENTHS)) {
      return { error: t('policies.allowanceInvalid', { max: MAX_ALLOWANCE_TENTHS }) }
    }
    return {
      body: {
        expectedVersion: row.version,
        // An empty map is no limits, which the server stores as null.
        discountLimitByRole: Object.keys(limits).length === 0 ? null : limits,
        returnWindowDays: window === '' ? null : Number(window),
        cutoutEdgingAllowanceTenthsMm: allowance === '' ? null : Number(allowance),
      },
    }
  }

  const save = async (): Promise<void> => {
    const shaped = shape()
    if ('error' in shaped) {
      setError(shaped.error)
      return
    }
    setError(null)
    setNotice(null)
    const write = (await enqueue({
      method: 'PATCH',
      path: POLICIES_PATH,
      label: t('policies.queueLabel'),
      body: shaped.body,
    })) as { id: string }
    setWriteId(write.id)
  }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.policies'))
  const inFlight = writeId === null ? undefined : snapshot.writes.find((w) => w.id === writeId)
  const conflicted = inFlight?.status === 'blocked' && inFlight.error?.code === 'VERSION_CONFLICT'

  const numberField = (
    id: string,
    labelKey: MessageKey,
    hintKey: MessageKey,
    value: string,
    onChange: (next: string) => void,
  ): JSX.Element => (
    <div className="lm-field">
      <label className="lm-field__label" htmlFor={id}>
        {t(labelKey)}
      </label>
      <input
        id={id}
        className="lm-input"
        type="text"
        inputMode="decimal"
        dir="ltr"
        autoComplete="off"
        aria-describedby={`${id}-hint`}
        value={value}
        disabled={busy}
        onChange={(e) => {
          onChange(e.currentTarget.value)
        }}
      />
      <p id={`${id}-hint`} className="lm-hint">
        {t(hintKey)}
      </p>
    </div>
  )

  return (
    <section className="lm-screen" aria-labelledby="policies-heading">
      <h1 id="policies-heading" className="lm-screen__heading">
        {t('policies.heading')}
      </h1>

      <DataSurface
        state={
          !canManage
            ? { kind: 'denied' }
            : loadFailed
              ? { kind: 'error', message: t('policies.loadFailed'), onRetry: refresh }
              : row === null
                ? { kind: 'loading' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={5}
        what={t('policies.what')}
        deniedMessage={t('policies.denied')}
      >
        <p className="lm-hint">{t('policies.means')}</p>

        <fieldset className="lm-fieldset">
          <legend className="lm-field__label">{t('policies.discountHeading')}</legend>
          <p className="lm-hint">{t('policies.discountMeans')}</p>
          {ROLE_FIELDS.map((field) => (
            <Fragment key={field.role}>
              {numberField(
                `policies-limit-${field.role}`,
                field.key,
                'policies.limitHint',
                draft.limits[field.role] ?? '',
                (next) => {
                  edit({ limits: { ...draft.limits, [field.role]: next } })
                },
              )}
            </Fragment>
          ))}
        </fieldset>

        <fieldset className="lm-fieldset">
          <legend className="lm-field__label">{t('policies.returnsHeading')}</legend>
          {numberField(
            'policies-window',
            'policies.window',
            'policies.windowMeans',
            draft.window,
            (next) => {
              edit({ window: next })
            },
          )}
        </fieldset>

        <fieldset className="lm-fieldset">
          <legend className="lm-field__label">{t('policies.cutoutHeading')}</legend>
          {numberField(
            'policies-allowance',
            'policies.allowance',
            'policies.allowanceMeans',
            draft.allowance,
            (next) => {
              edit({ allowance: next })
            },
          )}
        </fieldset>

        {error === null ? null : (
          <p className="lm-error" role="alert">
            {error}
          </p>
        )}
        {stillWaiting && writeId === null ? (
          <p className="lm-hint" role="status">
            {t('policies.stillWaiting')}
          </p>
        ) : null}
        {conflicted ? (
          <p className="lm-hint" role="alert">
            {t('policies.changedElsewhere')}
          </p>
        ) : null}

        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={busy}
          onClick={() => {
            void save()
          }}
        >
          {t('policies.save')}
        </button>

        {notice === null ? null : (
          <p className="lm-hint" role="status">
            {notice}
          </p>
        )}
        <p className="lm-hint">{line}</p>
      </DataSurface>
    </section>
  )
}
